import { useState, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface UsePullToRefreshHandlerOptions {
  /** Show error toast on refresh failure */
  showErrorToast?: boolean
  /** Custom error message */
  errorMessage?: string
  /** Show success toast on refresh success */
  showSuccessToast?: boolean
  /** Custom success message */
  successMessage?: string
}

interface UsePullToRefreshHandlerReturn {
  /** Whether the refresh is in progress */
  isRefreshing: boolean
  /** Handler to be passed to PullToRefresh component */
  handlePullRefresh: () => Promise<void>
  /** Manual trigger for refresh (same as handlePullRefresh) */
  handleRefresh: () => Promise<void>
}

/**
 * Unified hook for handling pull-to-refresh functionality
 * Manages refresh state, error handling, and optional toast notifications
 *
 * @example
 * ```tsx
 * // Simple usage
 * const { isRefreshing, handlePullRefresh } = usePullToRefreshHandler(refetch)
 *
 * // With error toast
 * const { isRefreshing, handlePullRefresh } = usePullToRefreshHandler(
 *   refetch,
 *   { showErrorToast: true }
 * )
 *
 * // With custom messages
 * const { isRefreshing, handlePullRefresh } = usePullToRefreshHandler(
 *   async () => await myCustomRefresh(),
 *   {
 *     showErrorToast: true,
 *     showSuccessToast: true,
 *     errorMessage: 'Failed to load data',
 *     successMessage: 'Data updated'
 *   }
 * )
 * ```
 */
export const usePullToRefreshHandler = (
  refreshFunction: () => Promise<void> | void,
  options: UsePullToRefreshHandlerOptions = {},
): UsePullToRefreshHandlerReturn => {
  const { t } = useTranslation()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const {
    showErrorToast = false,
    errorMessage,
    showSuccessToast = false,
    successMessage,
  } = options

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return

    setIsRefreshing(true)
    try {
      await refreshFunction()

      if (showSuccessToast) {
        toast.success(
          successMessage ||
            t('common:refreshSuccess') ||
            'Refreshed successfully',
        )
      }
    } catch (error) {
      console.error('Pull-to-refresh error:', error)

      if (showErrorToast) {
        toast.error(
          errorMessage || t('common:failedToRefresh') || 'Failed to refresh',
        )
      }
    } finally {
      setIsRefreshing(false)
    }
  }, [
    refreshFunction,
    isRefreshing,
    showErrorToast,
    errorMessage,
    showSuccessToast,
    successMessage,
    t,
  ])

  // Alias for consistency with component props
  const handlePullRefresh = handleRefresh

  return {
    isRefreshing,
    handlePullRefresh,
    handleRefresh,
  }
}
